type Role = 'teacher' | 'student';

export interface UiRedesignContext {
  pageKey?: string;
  role?: Role;
  teacherId?: number | null;
  studentId?: number | null;
}

let authContext: Omit<UiRedesignContext, 'pageKey'> = {};

const parseBoolean = (value: string | undefined, fallback = false): boolean => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;
  return ['1', 'true', 'yes', 'on'].includes(normalized);
};

const parseList = (value: string | undefined): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item, index, rows) => Boolean(item) && rows.indexOf(item) === index);
};

const parseNumberSet = (value: string | undefined): Set<number> => {
  const values = parseList(value)
    .map((entry) => Number(entry))
    .filter((entry) => Number.isInteger(entry) && entry > 0);
  return new Set(values);
};

const uiRedesignGlobalEnabled = parseBoolean(process.env.REACT_APP_UI_REDESIGN_V2, false);
const allowedPageKeys = new Set(parseList(process.env.REACT_APP_UI_REDESIGN_V2_PAGE_KEYS));
const allowedTeacherIds = parseNumberSet(process.env.REACT_APP_UI_REDESIGN_V2_PILOT_TEACHER_IDS);
const allowedStudentIds = parseNumberSet(process.env.REACT_APP_UI_REDESIGN_V2_PILOT_STUDENT_IDS);

// These student surfaces are released. Checklist eligibility remains class-scoped
// on the server; it must not depend on a test-account UI allowlist.
const releasedStudentPages = new Set([
  'v2_student_dashboard',
  'v2_student_run_presentation',
  'v2_student_assignment_detail',
  'v2_student_run_review',
  'v2_student_evidence_ledger',
]);

const normalizeActorContext = (
  context?: Omit<UiRedesignContext, 'pageKey'>,
): Omit<UiRedesignContext, 'pageKey'> => {
  if (context?.role === 'teacher') {
    return {
      role: 'teacher',
      teacherId: Number.isInteger(context.teacherId) ? Number(context.teacherId) : null,
      studentId: null,
    };
  }

  if (context?.role === 'student') {
    return {
      role: 'student',
      teacherId: null,
      studentId: Number.isInteger(context.studentId) ? Number(context.studentId) : null,
    };
  }

  return {};
};

export const setUiRedesignAuthContext = (context?: Omit<UiRedesignContext, 'pageKey'>): void => {
  authContext = normalizeActorContext(context);
};

export const isUiRedesignV2Enabled = (context?: UiRedesignContext): boolean => {
  if (uiRedesignGlobalEnabled) return true;
  if (context?.role === 'student' && context.pageKey && releasedStudentPages.has(context.pageKey)) return true;

  if (context?.pageKey && allowedPageKeys.has(context.pageKey)) {
    return true;
  }

  if (context?.role === 'teacher' && Number.isInteger(context.teacherId) && context.teacherId) {
    return allowedTeacherIds.has(Number(context.teacherId));
  }

  if (context?.role === 'student' && Number.isInteger(context.studentId) && context.studentId) {
    return allowedStudentIds.has(Number(context.studentId));
  }

  return false;
};

export const isUiRedesignPageEnabled = (pageKey: string, context?: Omit<UiRedesignContext, 'pageKey'>): boolean => {
  return isUiRedesignV2Enabled({
    ...authContext,
    ...normalizeActorContext(context),
    pageKey,
  });
};
